import type { Range } from 'slate';
import { Editor, Text } from 'slate';

import { Queries } from './queries-object';
import type { SuggestionMarkerEndElement, SuggestionMarkerStartElement } from '../types';
import { isSuggestionMarkerEndElement, isSuggestionMarkerStartElement } from '../types';

export interface SuggestionMarkerPair {
  end: SuggestionMarkerEndElement;
  start: SuggestionMarkerStartElement;
}

const suggestionMarkerQueries = {
  /**
   * Get all suggestion marker pairs in the document. Collects starts and ends
   * in a single traversal — this runs on normalize passes, so it must not
   * re-walk the document once per marker.
   */
  getAllSuggestionMarkers: (editor: Editor): SuggestionMarkerPair[] => {
    const startMarkers: SuggestionMarkerStartElement[] = [];
    const endMarkersByMarkerId = new Map<number, SuggestionMarkerEndElement>();

    for (const [node] of Editor.nodes(editor, {
      at: [],
      match: node => isSuggestionMarkerStartElement(node) || isSuggestionMarkerEndElement(node),
    })) {
      if (isSuggestionMarkerStartElement(node)) {
        startMarkers.push(node);
      } else if (isSuggestionMarkerEndElement(node) && !endMarkersByMarkerId.has(node.markerId)) {
        endMarkersByMarkerId.set(node.markerId, node);
      }
    }

    const pairs: SuggestionMarkerPair[] = [];
    for (const start of startMarkers) {
      const end = endMarkersByMarkerId.get(start.markerId);
      if (end) pairs.push({ end, start });
    }

    return pairs;
  },

  /**
   * Returns the next unused 1-based suggestion marker ID (1 if the document
   * has none). Pure and non-reserving: it derives `maxExistingId + 1` from the
   * current document, so repeated calls return the same value. Call sites that
   * allocate several IDs at once must increment locally.
   */
  getNextAvailableSuggestionMarkerId: (editor: Editor): number => {
    let maxId = 0;

    for (const [node] of Editor.nodes(editor, {
      at: [],
      match: node => isSuggestionMarkerStartElement(node) || isSuggestionMarkerEndElement(node),
    })) {
      if ('markerId' in node && typeof node.markerId === 'number') {
        maxId = Math.max(maxId, node.markerId);
      }
    }

    return maxId + 1;
  },

  /**
   * Get the start and end elements for a suggestion by its suggestionId.
   */
  getSuggestionById: (
    editor: Editor,
    suggestionId: string
  ): { end: SuggestionMarkerEndElement | null; start: SuggestionMarkerStartElement | null } => {
    const startEntry = Editor.nodes<SuggestionMarkerStartElement>(editor, {
      at: [],
      match: node => isSuggestionMarkerStartElement(node) && node.suggestionId === suggestionId,
    }).next().value;

    if (!startEntry) return { end: null, start: null };

    const [startMarker] = startEntry;

    const endEntry = Editor.nodes<SuggestionMarkerEndElement>(editor, {
      at: [],
      match: node => isSuggestionMarkerEndElement(node) && node.markerId === startMarker.markerId,
    }).next().value;

    if (!endEntry) return { end: null, start: startMarker };

    return { end: endEntry[0], start: startMarker };
  },

  /**
   * Get the Range for a suggestion by its suggestionId.
   */
  getSuggestionRange: (editor: Editor, suggestionId: string): Range | null => {
    const startEntry = Editor.nodes<SuggestionMarkerStartElement>(editor, {
      at: [],
      match: node => isSuggestionMarkerStartElement(node) && node.suggestionId === suggestionId,
    }).next().value;

    if (!startEntry) return null;

    const [startMarker, startPath] = startEntry;

    const endEntry = Editor.nodes<SuggestionMarkerEndElement>(editor, {
      at: [],
      match: node => isSuggestionMarkerEndElement(node) && node.markerId === startMarker.markerId,
    }).next().value;

    if (!endEntry) return null;

    const [, endPath] = endEntry;

    const start = Queries.getSelectablePoint(editor, {
      point: { offset: 0, path: [...startPath, 0] },
    });
    const end = Queries.getSelectablePoint(editor, {
      point: { offset: 0, path: [...endPath, 0] },
    });

    if (!start || !end) return null;

    return { anchor: start, focus: end };
  },

  /**
   * Get the text content within a suggestion range.
   */
  getSuggestionText: (editor: Editor, suggestionId: string): string | null => {
    const range = Queries.getSuggestionRange(editor, suggestionId);
    if (!range) return null;

    return Queries.string(editor, { at: range });
  },

  /**
   * Resolve the authorId for a suggestion by looking inside its range for the
   * first text node carrying a suggestedEdit and returning that edit's
   * authorId. Used to backfill missing authorIds on legacy suggestion markers
   * that were created before the field existed on SuggestionMarkerStartElement.
   */
  inferAuthorIdForSuggestion: (editor: Editor, suggestionId: string): string | null => {
    const range = Queries.getSuggestionRange(editor, suggestionId);
    if (!range) return null;

    for (const [node] of Editor.nodes(editor, { at: range, match: Text.isText })) {
      if (!Text.isText(node)) continue;
      const firstEdit = node.suggestedEdits?.[0];
      if (firstEdit) return firstEdit.authorId;
    }

    return null;
  },
};

export default suggestionMarkerQueries;
